import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Crown, Loader2, Trash2 } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { assetUrl } from "@/lib/prefs";

const MAX = 5 * 1024 * 1024;
const TYPES = ["image/jpeg", "image/png", "image/webp"];

export function planName(plan, role) {
  if (role === "admin") return "ADMIN";
  return plan?.startsWith("pro") ? "PRO" : (plan || "free").toUpperCase();
}

export function AccountHero({ data, onSaved }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [broken, setBroken] = useState(false);
  const initial = (data.name || data.email).trim().charAt(0).toUpperCase();

  const upload = async (file) => {
    if (!file) return;
    if (!TYPES.includes(file.type)) return toast.error("Use a JPG, PNG or WebP image");
    if (file.size > MAX) return toast.error("Image must be 5 MB or smaller");
    const form = new FormData();
    form.append("file", file);
    setBusy(true);
    try {
      const { data: view } = await api.post("/account/avatar", form);
      setBroken(false); onSaved(view); toast.success("Photo updated");
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); if (input.current) input.current.value = ""; }
  };
  const remove = async () => {
    setBusy(true);
    try { onSaved((await api.delete("/account/avatar")).data); toast.success("Photo removed"); }
    catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };

  return (
    <section className="as-hero" data-testid="account-hero">
      <div className="as-identity">
        <div className="as-avatar-wrap">
          <div className="as-avatar" data-testid="account-avatar">
            {data.picture && !broken
              ? <img src={assetUrl(data.picture)} alt="" referrerPolicy="no-referrer" onError={() => setBroken(true)} />
              : <span>{initial}</span>}
            {busy && <span className="as-avatar-busy"><Loader2 className="animate-spin" /></span>}
          </div>
          <button type="button" className="as-avatar-cam" onClick={() => input.current?.click()} disabled={busy} aria-label="Change photo" data-testid="avatar-cam-btn">
            <Camera size={15} />
          </button>
        </div>
        <div>
          <h2 data-testid="account-name">{data.name}</h2>
          <p data-testid="account-email">{data.username ? `@${data.username}` : data.email}</p>
          <span className="as-plan-pill" data-testid="account-plan"><Crown size={13} /> {planName(data.plan, data.role)}</span>
        </div>
      </div>
      <div className="as-avatar-actions">
        <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => upload(e.target.files?.[0])} data-testid="avatar-file-input" />
        <button type="button" className="as-secondary" disabled={busy} onClick={() => input.current?.click()} data-testid="avatar-upload-btn">
          <Camera size={15} /> {data.picture ? "Change photo" : "Upload photo"}
        </button>
        {data.picture && (
          <button type="button" className="as-secondary" disabled={busy} onClick={remove} data-testid="avatar-remove-btn"><Trash2 size={15} /> Remove</button>
        )}
        <small>JPG, PNG or WebP · max 5 MB</small>
      </div>
    </section>
  );
}
